import Campaign from 'mini-campaign/lib/store/Campaign';
import React, { HTMLAttributes } from 'react';
import { ActionType } from 'owa-lightning-core-v2';
import { CAMPAIGN_CLICKED, CAMPAIGN_DISMISSED } from 'mini-campaign/lib/utils/eventNames';
import { CampaignType } from 'mini-campaign/lib/store/schema/CampaignType';
import { CustomData } from 'mini-app-context';
import { endCampaign } from 'mini-campaign/lib/utils/endCampaign';
import { getDataLogger, LogOptions } from 'telemetry';
import { startCampaign } from 'mini-campaign/lib/utils/startCampaign';
import {
    AnchorProps,
    IconInfo,
    LazyEmbeddedMessageWrapper,
    CustomClassNames,
} from 'mini-embedded-messagebox';

interface CustomLogParamsType {
    logData?: CustomData;
    logOptions?: LogOptions;
}

export interface CampaignEmbeddedMessageConfig {
    title: string;
    subtitle: string;
    icon: IconInfo | Function;
    closeButtonAriaLabel: string;
    coverImageURL?: string;
    anchorProps?: AnchorProps;
    onCampaignDismiss?: () => void;
    onCampaignMessageClick?: () => void;
    onMount?: () => void;
    customLogParams?: CustomLogParamsType;
    withoutBubble?: boolean;
    customClassNames?: CustomClassNames;
    messageWrapperElementProps?: HTMLAttributes<HTMLDivElement>;
    rootElementProps?: HTMLAttributes<HTMLDivElement>;
}

/**
 * Class to instantiate a embedded message campaign type object.
 */
export class CampaignEmbeddedMessage implements CampaignType<CampaignEmbeddedMessageConfig> {
    logger = getDataLogger();

    public start(campaign: Campaign<CampaignEmbeddedMessageConfig>) {
        startCampaign(campaign);
    }

    public end(campaign: Campaign<CampaignEmbeddedMessageConfig>, actionType: ActionType) {
        endCampaign(campaign, actionType);
    }

    private logCampaignUsage(
        eventName: string,
        campaign: Campaign<CampaignEmbeddedMessageConfig>,
        customLogParams?: CustomLogParamsType
    ) {
        this.logger?.(
            eventName,
            { id: campaign.getId(), isUIAction: true, ...customLogParams?.logData },
            customLogParams?.logOptions
        );
    }

    private logAndEndCampaign(
        eventName: string,
        campaign: Campaign<CampaignEmbeddedMessageConfig>,
        actionType: ActionType,
        customLogParams?: CustomLogParamsType
    ) {
        // Log usage DP
        this.logCampaignUsage(eventName, campaign, customLogParams);

        // End the campaign
        this.end(campaign, actionType);
    }

    /**
     * This is called from the render function of the Campaign object
     * @param campaign Campaign object instance
     * @param viewProps Props to be passed to the view component
     */
    public render(
        campaign: Campaign<CampaignEmbeddedMessageConfig>,
        viewProps: CampaignEmbeddedMessageConfig
    ): JSX.Element {
        const self = this;
        const { onCampaignDismiss, onCampaignMessageClick, customLogParams } = viewProps;
        const { onUserDismiss, onCampaignClick } = campaign.actionTypes;

        const onCloseButtonClick = event => {
            event.stopPropagation();

            onCampaignDismiss?.();
            self.logAndEndCampaign(CAMPAIGN_DISMISSED, campaign, onUserDismiss, customLogParams);
        };

        const onMessageClick = (event: React.SyntheticEvent<EventTarget>) => {
            if (!onCampaignClick) {
                return null;
            }
            event.stopPropagation();

            onCampaignMessageClick?.();
            self.logAndEndCampaign(CAMPAIGN_CLICKED, campaign, onCampaignClick, customLogParams);
        };

        return (
            <LazyEmbeddedMessageWrapper
                {...viewProps}
                onCloseButtonClick={onCloseButtonClick}
                onMessageClick={onMessageClick}
            />
        );
    }
}
